import $ from 'jquery';

import { readOnlyProperty } from '@lts-web/utils/ObjectUtil';
import TextBundleUtil from '@lts-web/utils/TextBundleUtil';
import SearchModuleUtil from '@lts-web/utils/SearchModuleUtil';
import FormDataSchema from '@lts-web/schemas/FormDataSchema';
import LocationUtil, { DESTINATION_ALL, ORIGIN_ALL } from '@lts-web/utils/LocationUtil';
import { UNKNOWN, CALENDAR_DATE } from "@lts-web/types/DefaultDateTypes";
import { GDS_PACKAGE, LDS_PACKAGE } from "@lts-web/types/SystemTypes";
import * as dataSets from '@lts-web/schemas/formDataSets';

class FormData {
    constructor (data = {}) {
        this._data = FormDataSchema(data);

        readOnlyProperty(this, '_data');
        readOnlyProperty(this._data, 'moduleName');
        readOnlyProperty(this._data, 'formElement');
    }

    getModuleName () {
        return this._data.moduleName;
    }

    getFormElement () {
        return this._data.formElement;
    }

    getActionURL () {
        return this._data.actionURL;
    }

    setActionURL (actionURL) {
        if (typeof actionURL !== 'string')
            throw new TypeError('First argument must be of type `string`');

        this._data.actionURL = actionURL;
    }

    getDefaults () {
        return this._data.defaults;
    }

    setDefaults (data) {
        if (typeof data !== 'object')
            throw new TypeError('First argument must be of type `object`');

        this._data.defaults = data;
    }

    getInputsConvention () {
        return this._data.inputsConvention;
    }

    setInputsConvention (inputsConvention) {
        if (typeof inputsConvention !== 'object')
            throw new TypeError('First argument must be of type `object`');

        this._data.inputsConvention = inputsConvention;
    }

    getNavigationFunction () {
        return this._data.navigationFunction;
    }

    setNavigationFunction (navigationFunction) {
        if (typeof navigationFunction !== 'function')
            throw new TypeError('First argument must be of type `function`');

        this._data.navigationFunction = navigationFunction;
    }

    findDefaultValues (nameList) {
        if (!Array.isArray(nameList))
            throw new TypeError('First argument must be of type `array`');

        const values = {};
        const length = nameList.length;

        if (length > 0) {
            for (let i = 0; i < length; i++) {
                const key = nameList[i];
                const value = this.getDefaultValue(key);

                values[key] = value || '';
            }
        }

        return values;
    }

    getDefaultValue (key) {
        const { defaults } = this._data;
        return defaults[key];
    }

    getAllValues () {
        return this.findValues(this.getInputKeys());
    }

    findValues (nameList) {
        if (!Array.isArray(nameList))
            throw new TypeError('First argument must be of type `array`');

        const values = {};
        const length = nameList.length;

        if (length > 0) {
            for (let i = 0; i < length; i++) {
                const key = nameList[i];
                const value = this.getValue(key);

                if (typeof value !== 'undefined') {
                    values[key] = value;
                }
            }
        }

        return values;
    }

    updateValues (data) {
        if (typeof data !== 'object')
            throw new TypeError('First argument must be of type object');

        for (let key in data) {
            this.setValue(key, data[key]);
        }
    }

    getValue (key) {
        const { inputsConvention } = this._data;
        return this.getValueByName(inputsConvention[key]);
    }

    setValue (key, value) {
        const { inputsConvention } = this._data;

        if (!inputsConvention.hasOwnProperty(key))
            return false;

        this.setValueByName(inputsConvention[key], value);
        return true;
    }

    getValueByName (name) {
        let inputs = this.getInputByName(name);

        if (!inputs[0]) {
            return '';
        }

        if (inputs.length > 1) {
            const type = inputs[0].getAttribute('type');

            if (type === 'checkbox' || type === 'radio') {
                inputs = [...inputs].filter(element => element.matches(':checked'));
            }
        }

        return inputs[0].value;
    }

    setValueByName (name, value) {
        const inputs = this.getInputByName(name);

        [...inputs].forEach(element => {
            element.value = value;
        });
    }

    getFormName () {
        const { formElement } = this._data;
        return formElement.getAttribute('name');
    }

    getInputByKey (key) {
        const { inputsConvention } = this._data;
        return this.getInputByName(inputsConvention[key] || '');
    }

    getInputByName (inputName) {
        const { formElement } = this._data;
        return formElement.querySelectorAll(`[name="${inputName}"]`);
    }

    getInputKeys () {
        return Object.keys(this._data.inputsConvention);
    }

    getInputNames () {
        return Object.values(this._data.inputsConvention);
    }

    submit (param, method) {
        switch (method) {
            case 'POST':
                this.requestPOST();
                break;
            default:
                this.requestGET(param);
                break;
        }
    }

    requestGET (param) {
        const {
            actionURL,
            formElement,
            navigationFunction
        } = this._data;
        const formId = formElement.getAttribute('id');

        navigationFunction(actionURL, formId, param || '');
    }

    requestPOST () {
        const { actionURL, formElement } = this._data;

        formElement.setAttribute('action', actionURL);
        formElement.submit();
    }
}

/**
 * Validation messages
 * @name messages
 * @type {Object}
 */
export const messages = Object.freeze({
    '000': dataSets.message('000', 'OK'),
    '001': dataSets.message(
        '001',
        'dates',
        'Invalid dates: Depart date is required',
        () => TextBundleUtil.getText('departDate')
    ),
    '002': dataSets.message(
        '002',
        'dates',
        'Invalid dates: Return date is required',
        () => TextBundleUtil.getText('returnDate')
    ),
    '003': dataSets.message(
        '003',
        'dates',
        'Invalid dates: Depart and return dates cannot be empty or equal',
        () => TextBundleUtil.getText('returnDate')
    ),
    '004': dataSets.message(
        '004',
        'dates',
        'Invalid dates: Depart and return dates must be equal',
        () => 'Depart and return dates must be equal'
    ),
    '005': dataSets.message(
        '005',
        'locations',
        'Invalid destination: Destination is required',
        () => TextBundleUtil.getText('destPopup.H2.SelectDetonation')
    ),
    '006': dataSets.message(
        '006',
        'locations',
        'Invalid depart location: Depart location is required',
        () => 'Depart location is required'
    ),
    '007': dataSets.message(
        '007',
        'locations',
        'Invalid destination: Destination does not match the chosen dates',
        () => TextBundleUtil.getText('destPopup.H2.SelectDetonation')
    ),
    '008': dataSets.message(
        '008',
        'passengers',
        'Invalid passengers: Invalid passenger combination',
        () => TextBundleUtil.getText('israel.passangerCombinationNotAllowed')
    ),
    '009': dataSets.message(
        '009',
        'passengers',
        'Invalid passengers: Number of infants cannot be larger then the number of adults',
        () => TextBundleUtil.getText('engine.error.tooManyInfants')
    ),
    '010': dataSets.message(
        '010',
        'passengers',
        'Unavailable seats: Number of passenger cannot be larger then the number of seats available',
        () => TextBundleUtil.getText('israir.summaryEngine.notEnoughPlaces')
    )
});

/**
 * Validation helpers
 * @name helpers
 * @type {Object}
 */
export const helpers = Object.freeze({
    isDefinedDatesRequired: function (moduleName, { dateType }) {
        const searchModule = SearchModuleUtil.getModule(moduleName);

        return (
            searchModule.isDefinedDatesRequired() &&
            (
                dateType === UNKNOWN ||
                dateType === CALENDAR_DATE
            )
        );
    },
    isSpecificDatesRequired: function (moduleName, dataSet) {
        const searchModule = SearchModuleUtil.getModule(moduleName);
        const includesLDS = LocationUtil.includesSystemType(moduleName, dataSet.destination, LDS_PACKAGE);

        return (
            (searchModule.isSpecificDatesRequired()) ||
            (helpers.isAbroadModuleSubjectSelected(moduleName, dataSet)) ||
            (helpers.isMatrixSearch(dataSet)) ||
            (!includesLDS && !helpers.isOpenDestSearch(dataSet)) ||
            dataSet.roomNum > 1
        );
    },
    isAbroadModuleSubjectSelected: function (moduleName, { subject = '' } = {}) {
        return (moduleName === 'abroadPackage' || moduleName === 'abroadFlight') && subject && subject !== 'ALL';
    },
    shouldDisableUnavailableDates: function (moduleName, { destination }) {
        const searchModule = SearchModuleUtil.getModule(moduleName);
        const destList = helpers.generateDestinationData(moduleName, destination)
        let hasGDSDestination = destList.raw.cityCode.some((destination) => {
            return helpers.isGDSDestination(moduleName, { destination })
        }) || window.is2OW;

        return (
            searchModule.isDatesPaintingSupported() &&
            !(
                searchModule.isOpenCalendarSupported() &&
                hasGDSDestination
            )
        )
    },
    isGDSDestination: function (moduleName, { destination }) {
        return LocationUtil.includesSystemType(moduleName, destination, GDS_PACKAGE);
    },
    isCalendarDate: function ({ dateType }) {
        return dateType === CALENDAR_DATE;
    },
    isRoundTrip: function ({ routeType }) {
        return routeType === '2';
    },
    isOneWay: function ({ routeType }) {
        return routeType === '1';
    },
    isSameDates: function ({ departDate, returnDate }) {
        return departDate !== '' && departDate === returnDate;
    },
    isDifferentDates: function ({ departDate, returnDate }) {
        return departDate !== '' && departDate !== returnDate;
    },
    isValidMonth: function ({ departDate, dateType }) {
        return dateType !== 'calendarDate'|| departDate !== ''
    },
    isMatrixSearch: function ({ matrixSearch }) {
        return matrixSearch === true || matrixSearch === 'true';
    },
    isFromAbroad: function ({ fromAbroadFlight }) {
        return fromAbroadFlight === true || fromAbroadFlight === 'true';
    },
    isDepartValid: function (moduleName, { departure }) {
        return moduleName === 'domesticHotel' || LocationUtil.isLocationValid(moduleName, departure);
    },
    isDestValid: function (moduleName, { destination }) {
        return LocationUtil.isLocationValid(moduleName, destination);
    },
    isOpenDestSearch: function ({ destination }) {
        return destination === DESTINATION_ALL;
    },
    isOpenDepartSearch: function ({ departure }) {
        return departure === ORIGIN_ALL;
    },
    isPaxNumValid: function ({ adultsNum, babyNum }) {
        return !(adultsNum < babyNum);
    },
    isPaxCombinationValid: function ({ adultsNum, childrenNum }) {
        const allowedPaxCombination = TextBundleUtil.getText('allowedPaxCombination') || '';
        return allowedPaxCombination.split(",").includes(adultsNum + "+" + childrenNum);
    },
    generateDestinationData: function (module, destination){
        let cityName = [];
        let cityCode = [];
        let locationType = [];
        let destList = !Array.isArray(destination) ? destination.split(',') : destination;

        destList.forEach(function (dest) {
            const location = LocationUtil.getLocationByCity(module, dest);

            if (location === null) {
                return;
            }

            cityName.push(location.getCityName());
            cityCode.push(location.getCityCode());
            locationType.push(location.getLocationType());
        });

        return {
            cityName: cityName.join(','),
            cityCode: cityCode.join(','),
            locationType: locationType.join(','),
            raw: {
                cityName,
                cityCode,
                locationType
            },
            size: cityCode.length
        };
    },
});

/**
 * Unified data input validators
 * @name validators
 * @type {Object}
 */
export const validators = Object.freeze({
    dates: function (searchModule, formData, calendarSelection) {
        const moduleName = searchModule.getName();

        if (searchModule.getCalendarType() === 'months') {
            if (!helpers.isValidMonth(formData))
                return messages['001'];

            return messages['000'];
        }

        if (
            helpers.isSpecificDatesRequired(moduleName, formData) ||
            helpers.isDefinedDatesRequired(moduleName, formData)
        ) {
            if (helpers.isRoundTrip(formData)) {
                if (!$.DatesUtil.isValidDate(formData.departDate, '/'))
                    return messages['001'];

            } else if (helpers.isOneWay(formData)) {
                if (!$.DatesUtil.isValidDate(formData.departDate, '/'))
                    return messages['001'];

                if (helpers.isDifferentDates(formData))
                    return messages['004'];
            }
        }

        if (
            helpers.isRoundTrip(formData) &&
            helpers.isCalendarDate(formData) &&
            $.DatesUtil.isValidDate(formData.departDate, '/') &&
            (
                !searchModule.sameDayReturn() ||
                calendarSelection < 2
            ) &&
            (
                helpers.isSameDates(formData) ||
                !$.DatesUtil.isValidDate(formData.returnDate, '/')
            )
        ) return messages['003'];

        //OK
        return messages['000'];
    },

    locations: function (searchModule, formData) {
        const moduleName = searchModule.getName();

        if (
            (
                !helpers.isDestValid(moduleName, formData)
                || helpers.isOpenDestSearch(formData)
            )
            &&
            (
                searchModule.isDefinedDestRequired()
                // || helpers.isFromAbroad(formData)
            )
        ) return messages['005'];

        if (!helpers.isDepartValid(moduleName, formData))
            return messages['006'];

        //OK
        return messages['000'];
    },

    passengers: function (searchModule, formData) {
        const moduleName = searchModule.getName();

        if (moduleName === 'domesticPackage' && !helpers.isPaxCombinationValid(formData))
            return messages['008'];

        if (searchModule.isShowInfants() && !helpers.isPaxNumValid(formData))
            return messages['009'];

        //OK
        return messages['000'];
    }
});

FormData.messages = messages;
FormData.helpers = helpers;
FormData.validators = validators;

export default FormData;